import React from 'react';
import { useLang } from '../context/LanguageContext';
import { Shield, Award, Handshake } from 'lucide-react';

const text = {
  overline: { pl: 'Zaufanie', en: 'Trust', ua: 'Довіра' },
  title: {
    pl: 'Budujemy relacje oparte na zaufaniu i odpowiedzialności',
    en: 'We build relationships based on trust and responsibility',
    ua: 'Ми будуємо відносини, засновані на довірі та відповідальності',
  },
  desc: {
    pl: 'Nasi klienci powierzają nam kluczowe procesy operacyjne. Traktujemy to jako zobowiązanie — dostarczamy wyniki, dotrzymujemy terminów i dbamy o najwyższe standardy.',
    en: 'Our clients entrust us with key operational processes. We treat this as a commitment — we deliver results, meet deadlines and maintain the highest standards.',
    ua: 'Наші клієнти довіряють нам ключові операційні процеси. Ми ставимося до цього як до зобов\'язання — досягаємо результатів, дотримуємось термінів та підтримуємо найвищі стандарти.',
  },
  stats: [
    { icon: 'Shield', value: '100%', label: { pl: 'Terminowość realizacji', en: 'On-time delivery', ua: 'Своєчасність виконання' } },
    { icon: 'Award', value: '50+', label: { pl: 'Zrealizowanych projektów', en: 'Completed projects', ua: 'Реалізованих проєктів' } },
    { icon: 'Handshake', value: '95%', label: { pl: 'Klientów kontynuuje współpracę', en: 'Clients continue cooperation', ua: 'Клієнтів продовжують співпрацю' } },
  ],
};

const iconMap = { Shield, Award, Handshake };

const TrustSection = () => {
  const { lang } = useLang();

  return (
    <section data-testid="trust-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left - Image */}
          <div className="aspect-[4/3] overflow-hidden bg-[#F8FAFC]">
            <img
              src="https://images.unsplash.com/photo-1758599543152-a73184816eba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMGhhbmRzaGFrZSUyMHBhcnRuZXJzaGlwJTIwdHJ1c3QlMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzc2MzQwMjYyfDA&ixlib=rb-4.1.0&q=85"
              alt="Business partnership handshake"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>

          {/* Right - Content */}
          <div>
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#2563EB] font-['Manrope'] mb-4">
              {text.overline[lang]}
            </span>
            <h2
              data-testid="trust-title"
              className="font-['Outfit'] text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#0F172A] leading-tight mb-6"
            >
              {text.title[lang]}
            </h2>
            <p className="font-['Manrope'] text-base md:text-lg text-[#475569] leading-relaxed mb-10">
              {text.desc[lang]}
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6">
              {text.stats.map((stat, i) => {
                const Icon = iconMap[stat.icon];
                return (
                  <div key={i} data-testid={`trust-stat-${i}`} className="text-center">
                    <div className="w-10 h-10 flex items-center justify-center border border-[#E2E8F0] mx-auto mb-3">
                      <Icon className="w-5 h-5 text-[#2563EB]" strokeWidth={1.5} />
                    </div>
                    <div className="font-['Outfit'] text-2xl md:text-3xl font-black text-[#0F172A] mb-1">
                      {stat.value}
                    </div>
                    <div className="font-['Manrope'] text-xs text-[#475569] leading-snug">
                      {stat.label[lang]}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustSection;
